import { BiometricCheckProgress, Country, ErrorCode, IdentityCheckStatus } from '@afterpaytouch/portal-api'
import { CheckoutError, TerminalErrorCode, getCheckoutIdentityError } from './checkout'
import { Route } from '../config/router'
import { ConsumerAccountIdentitiesResponse } from '@afterpaytouch/portal-api/consumer/identityBiometric'
import { isCountryNeedsBiometricVerification, isCountryNeedsSoftIdAttempt, isCountryUsePersonaFlow } from '.'

export type IdentityDocument = 'licence' | 'medicare' | 'passport'
export type IdentityPiece = 'address'

export const errorsEligibleForHardIdVerification: ErrorCode[] = [
  ErrorCode.HardIdCollectionRequired,
  ErrorCode.HardIdCollectionRequiredNoWaitResponse,
  ErrorCode.IdCheckRequired,
  ErrorCode.MaxAttemptsReached,
  ErrorCode.MaxAttemptsReachedForType,
]

export const errorsEligibleForVerifyRetryOrConfirmDetails: ErrorCode[] = [ErrorCode.IncompleteConsumerProfile]

export const terminalIdvErrors: ErrorCode[] = [
  ErrorCode.DuplicateIdentity,
  ErrorCode.HighRiskNationality,
  ErrorCode.IdentityCheckGatewayError,
  ErrorCode.IdentityCheckRejected,
]

export interface DocumentProps {
  onSuccess?: () => void
  onFailure?: () => void
}

export const toIdentityDocument = (str?: string | string[]): IdentityDocument | null => {
  if (Array.isArray(str)) {
    str = str[0]
  }
  if (typeof str === 'undefined') {
    return null
  }
  switch (str) {
    case 'medicare':
    case 'passport':
    case 'licence':
      return str
    default:
      return null
  }
}

export const getIdentityVerificationRoute = (
  country: Country,
  flags: Record<string, boolean | string>,
  errorCode?: ErrorCode,
  identityResponse?: ConsumerAccountIdentitiesResponse
): Route | null => {
  const needsSoftIdAttempt = isCountryNeedsSoftIdAttempt(country)
  const needsBiometricVerification = isCountryNeedsBiometricVerification(country)
  const isPersonaFlow = isCountryUsePersonaFlow(country)
  const { enableRedirectionIncompleteConsumerProfile, softIdRetryEnabled } = flags
  const isIncompleteConsumerProfileError: boolean = errorCode === ErrorCode.IncompleteConsumerProfile
  const isRedirectionEnableForIncompleteProfileError: boolean = Boolean(
    enableRedirectionIncompleteConsumerProfile && isIncompleteConsumerProfileError && needsBiometricVerification
  )

  const isHardIdCollectionRequiredError: boolean = errorCode === ErrorCode.HardIdCollectionRequired
  const isHardIdCollectionRequiredNoWaitResponseError: boolean = errorCode === ErrorCode.HardIdCollectionRequiredNoWaitResponse

  if (errorCode === ErrorCode.IdCheckRequired) {
    if (country === Country.AU && softIdRetryEnabled !== true) {
      return Route.VERIFY_IDENTITY
    }

    if (needsSoftIdAttempt) {
      // Only happen for UK CA and AU users (preserve existing logic)
      if (identityResponse?.identityCheckMetadata?.profileCheckAttemptsLeft === true) {
        return Route.VERIFY_RETRY
      }
      // AU hard idv flow
      if (!needsBiometricVerification) {
        return Route.VERIFY_IDENTITY
      }
    } else {
      // For NZ hard idv flow (AU now handled above)
      return Route.VERIFY_IDENTITY
    }
  }

  if (isRedirectionEnableForIncompleteProfileError) {
    return Route.VERIFY_RETRY
  }

  if (isHardIdCollectionRequiredError || isHardIdCollectionRequiredNoWaitResponseError) {
    return Route.VERIFY_IDENTITY
  }

  // CA user never got PARTIAL_VERIFIED
  if (identityResponse?.identityCheckStatus === IdentityCheckStatus.PARTIAL_VERIFIED) {
    return Route.VERIFY_IDENTITY
  }

  if (identityResponse?.identityCheckStatus === IdentityCheckStatus.NOT_REQUIRED || identityResponse?.identityCheckStatus === IdentityCheckStatus.VERIFIED) {
    return null
  }

  if (identityResponse?.identityCheckMetadata?.profileCheckAttemptsLeft === true) {
    return Route.VERIFY_RETRY
  }

  if (identityResponse?.biometricCheckEnabled) {
    if (
      identityResponse?.biometricCheckProgress === BiometricCheckProgress.NOT_STARTED ||
      identityResponse?.biometricCheckProgress === BiometricCheckProgress.CHECK_TERMINATED ||
      (isPersonaFlow && identityResponse?.biometricCheckProgress === BiometricCheckProgress.CHECK_SUBMITTED)
    ) {
      return Route.VERIFY_IDENTITY_START
    } else {
      return Route.VERIFYING_BIOMETRIC
    }
  }

  return Route.TERMINAL_ERROR
}

export interface HandleIdentityCheckDeclineErrorArgs {
  errorCode: string
}

export interface HandleIdentityCheckDeclineErrorResponse {
  route?: string
  checkoutError?: CheckoutError
}

export const handleIdentityCheckDeclineError = ({ errorCode }: HandleIdentityCheckDeclineErrorArgs): HandleIdentityCheckDeclineErrorResponse => {
  const errorCheckout: CheckoutError = getCheckoutIdentityError(errorCode)
  return {
    checkoutError: errorCheckout,
    route: '/oh-no',
  }
}

export interface HandleStartCheckoutErrorRequest {
  errorCode: ErrorCode
  identitiesResult?: ConsumerAccountIdentitiesResponse
  needsBiometricVerification: boolean
  flags: Record<string, boolean | string>
}

export interface HandleStartCheckoutIdentityErrorResponse {
  route?: string
  checkoutError?: CheckoutError
}
export const handleStartCheckoutIdentityError = ({ country, flags, errorCode, identitiesResult }): HandleStartCheckoutIdentityErrorResponse | void => {
  if (typeof identitiesResult !== 'undefined' && identitiesResult.identityCheckStatus === IdentityCheckStatus.FAILED) {
    return {
      route: Route.TERMINAL_ERROR,
      checkoutError: {
        error: TerminalErrorCode.IdentityVerificationFailedManual,
      },
    }
  }

  if (terminalIdvErrors.includes(errorCode)) {
    return handleIdentityCheckDeclineError({
      errorCode,
    })
  }
  if (
    [...errorsEligibleForHardIdVerification, ...errorsEligibleForVerifyRetryOrConfirmDetails].includes(errorCode) ||
    typeof identitiesResult !== 'undefined'
  ) {
    const url = getIdentityVerificationRoute(country, flags, errorCode, identitiesResult)
    if (url !== null) {
      if (url === Route.TERMINAL_ERROR) {
        const errorCheckout: CheckoutError = getCheckoutIdentityError(errorCode)
        return { route: url, checkoutError: errorCheckout }
      }
      return { route: url }
    }
  }
}
